import { useMemo } from 'react'
import { PromotionResourceBadge } from '@/components/promocode/PromotionResourceBadge'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Separator } from '@/components/ui/separator'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { OrderDetail } from '@/lib/api/orders'
import { useStorePromotions, PromotionStoreItem } from '@/lib/api/promotions'
import { formatMoney } from '@/lib/money'
import { useTranslation } from 'react-i18next'
import { toIncludedTaxDisplay } from '@/app/orders/[id]/utils/included-tax'

type OrderSummary = OrderDetail['summaries'][number]
type SummaryAdjustment = NonNullable<OrderSummary['adjustments']>[number]

interface NormalizedPromotion {
	sourceId: string
	description: string
	amount: number
	currency: string
}

interface LineItemPromotion extends NormalizedPromotion {
	productName: string
	variantName: string
}

interface OrderDiscountCardProps {
	order: OrderDetail
}

type PromotionLookup = Record<string, PromotionStoreItem>

const normalizeAdjustment = (adj: SummaryAdjustment, fallbackCurrency: string): NormalizedPromotion => ({
	sourceId: adj.sourceId ?? '',
	description: adj.description ?? '',
	amount: adj.amount ?? 0,
	currency: adj.currency ?? fallbackCurrency,
})

// Read through the included-tax display so an order discount shows the amount the merchant set (#TAG-3345).
const collectOrderPromotions = (order: OrderDetail): NormalizedPromotion[] =>
	order.summaries.flatMap((summary) =>
		(toIncludedTaxDisplay(summary).adjustments ?? [])
			.filter((adj) => adj.type === 'Promotion')
			.map((adj) => normalizeAdjustment(adj, order.currency))
	)

const collectLineItemPromotions = (order: OrderDetail): LineItemPromotion[] =>
	order.summaries.flatMap((summary) => {
		const items = (summary.items ?? []) as Array<Record<string, any>>
		return items.flatMap((item) => {
			const adjustments = (item.adjustments ?? []) as SummaryAdjustment[]
			return adjustments
				.filter((adj) => adj.type === 'Promotion')
				.map((adj) => ({
					...normalizeAdjustment(adj, order.currency),
					productName: String(item.orderLineItemProduct?.name ?? item.product?.name ?? ''),
					variantName: String(item.orderLineItemVariant?.name ?? item.variant?.name ?? ''),
				}))
		})
	})

const collectUniquePromotionIds = (
	orderPromotions: NormalizedPromotion[],
	lineItemPromotions: LineItemPromotion[],
): string[] => {
	const allIds = [...orderPromotions, ...lineItemPromotions]
		.map((p) => p.sourceId)
		.filter(Boolean)
	return [...new Set(allIds)]
}

const buildPromotionLookup = (
	promotions: PromotionStoreItem[],
): PromotionLookup =>
	Object.fromEntries(promotions.map((p) => [p.id, p]))

const PromotionCodeBadge = ({ code }: { code: string }) => (
	<Badge variant="outline" className="font-mono text-xs">
		{code}
	</Badge>
)

const PromotionRow = ({
	sourceId,
	description,
	amount,
	currency,
	promotionDetail,
	storeId,
}: NormalizedPromotion & { promotionDetail?: PromotionStoreItem; storeId: string }) => (
	<TableRow>
		<TableCell>
			{sourceId && <PromotionResourceBadge promotionId={sourceId} storeId={storeId} />}
		</TableCell>
		<TableCell className="font-medium">
			{promotionDetail?.name ?? description}
		</TableCell>
		<TableCell>
			{promotionDetail?.code && (
				<PromotionCodeBadge code={promotionDetail.code} />
			)}
		</TableCell>
		<TableCell className="text-right font-semibold tabular-nums text-green-600 dark:text-green-400">
			{formatMoney(amount, currency)}
		</TableCell>
	</TableRow>
)

const lineItemSubtitle = (productName: string, variantName: string) =>
	[productName, variantName].filter(Boolean).join(' — ')

const LineItemPromotionRow = ({
	productName,
	variantName,
	...promo
}: LineItemPromotion & { promotionDetail?: PromotionStoreItem; storeId: string }) => (
	<>
		<PromotionRow {...promo} />
		{(productName || variantName) && (
			<TableRow className="border-0">
				<TableCell />
				<TableCell colSpan={3} className="pt-0 pb-2 text-xs text-muted-foreground">
					{lineItemSubtitle(productName, variantName)}
				</TableCell>
			</TableRow>
		)}
	</>
)

export const OrderDiscountCard = ({ order }: OrderDiscountCardProps) => {
	const { t } = useTranslation('orders')
	const orderPromotions = collectOrderPromotions(order)
	const lineItemPromotions = collectLineItemPromotions(order)
	const totalCount = orderPromotions.length + lineItemPromotions.length
	const hasOrderPromotions = orderPromotions.length > 0
	const hasLineItemPromotions = lineItemPromotions.length > 0

	const promotionIds = useMemo(
		() => collectUniquePromotionIds(orderPromotions, lineItemPromotions),
		[orderPromotions, lineItemPromotions],
	)

	const { data: promotionsData } = useStorePromotions(
		{ storeId: order.storeId, ids: promotionIds },
		promotionIds.length > 0,
	)

	const promotionLookup = useMemo(
		() => buildPromotionLookup(promotionsData?.promotions ?? []),
		[promotionsData],
	)

	return (
		<Card>
			<CardHeader>
				<CardTitle>{t('discounts')}</CardTitle>
				<CardDescription>
					{t('promotions.appliedCount', { count: totalCount })}
				</CardDescription>
			</CardHeader>
			<CardContent className="space-y-4">
				{hasOrderPromotions && (
					<div className="space-y-2">
						<Badge variant="purple">{t('promotions.orderLevel')}</Badge>
						<div className="rounded-md border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t('promotions.columns.id')}</TableHead>
										<TableHead>{t('promotions.columns.name')}</TableHead>
										<TableHead>{t('promotions.columns.code')}</TableHead>
										<TableHead className="text-right">{t('promotions.columns.amount')}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{orderPromotions.map((promo, idx) => (
										<PromotionRow
											key={promo.sourceId || idx}
											{...promo}
											storeId={order.storeId}
											promotionDetail={promotionLookup[promo.sourceId]}
										/>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
				)}

				{hasOrderPromotions && hasLineItemPromotions && <Separator />}

				{hasLineItemPromotions && (
					<div className="space-y-2">
						<Badge variant="teal">{t('promotions.lineItemLevel')}</Badge>
						<div className="rounded-md border">
							<Table>
								<TableHeader>
									<TableRow>
										<TableHead>{t('promotions.columns.id')}</TableHead>
										<TableHead>{t('promotions.columns.name')}</TableHead>
										<TableHead>{t('promotions.columns.code')}</TableHead>
										<TableHead className="text-right">{t('promotions.columns.amount')}</TableHead>
									</TableRow>
								</TableHeader>
								<TableBody>
									{lineItemPromotions.map((promo, idx) => (
										<LineItemPromotionRow
											key={promo.sourceId || idx}
											{...promo}
											storeId={order.storeId}
											promotionDetail={promotionLookup[promo.sourceId]}
										/>
									))}
								</TableBody>
							</Table>
						</div>
					</div>
				)}

				{!hasOrderPromotions && !hasLineItemPromotions && (
					<p className="text-sm text-muted-foreground text-center py-4">
						{t('promotions.noDiscounts')}
					</p>
				)}
			</CardContent>
		</Card>
	)
}
